import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Globe, Users, Building2, Wallet, Check, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import WalletConnectModal from '@/components/WalletConnectModal';
import { toast } from 'sonner';

const userTypes = [
  { id: 'donor', icon: Heart, title: 'Donor', desc: 'Support verified causes with trackable donations' },
  { id: 'ngo', icon: Globe, title: 'NGO', desc: 'Showcase your impact with on-chain verification' },
  { id: 'volunteer', icon: Users, title: 'Volunteer', desc: 'Find opportunities and build your impact portfolio' },
  { id: 'corporate', icon: Building2, title: 'Corporate Partner', desc: 'Demonstrate ESG impact with immutable proof' },
];

export default function SignUpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || 'donor');
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '', orgName: '', companyName: '', jobTitle: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    login({
      name: formData.name, email: formData.email, type: selectedType,
      username: formData.name.toLowerCase().replace(/\s+/g, '-'),
      walletAddress, orgName: formData.orgName, companyName: formData.companyName,
    });
    navigate('/onboarding');
  };

  const handleWalletConnect = (address) => {
    setWalletConnected(true);
    setWalletAddress(address);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center py-12 px-4" data-testid="signup-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-[#111827] tracking-tight mb-2">Join HumaClub</h1>
          <p className="text-[#4B5563] font-body">Start making verified impact today</p>
        </div>

        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-6 sm:p-8">
          <div className="mb-6">
            <label className="text-sm font-medium text-[#111827] font-heading block mb-3">I want to join as</label>
            <div className="grid grid-cols-2 gap-2">
              {userTypes.map(ut => (
                <button key={ut.id} onClick={() => setSelectedType(ut.id)} className={`p-3 rounded-xl text-left transition-all border-2 ${selectedType === ut.id ? 'border-transparent bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white' : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`} data-testid={`type-${ut.id}`}>
                  <ut.icon className={`w-5 h-5 mb-1.5 ${selectedType === ut.id ? 'text-white' : 'text-[#7C3AED]'}`} />
                  <p className={`font-heading font-semibold text-sm ${selectedType === ut.id ? 'text-white' : 'text-[#111827]'}`}>{ut.title}</p>
                  <p className={`text-xs mt-0.5 ${selectedType === ut.id ? 'text-white/70' : 'text-gray-500'} font-body`}>{ut.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Full Name</label>
              <Input value={formData.name} onChange={(e) => updateField('name', e.target.value)} placeholder="Your full name" className="rounded-xl border-gray-200 font-body" required data-testid="signup-name" />
            </div>
            {selectedType === 'ngo' && (
              <div>
                <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Organization Name</label>
                <Input value={formData.orgName} onChange={(e) => updateField('orgName', e.target.value)} placeholder="Your organization" className="rounded-xl border-gray-200 font-body" required data-testid="signup-org" />
              </div>
            )}
            {selectedType === 'corporate' && (
              <>
                <div>
                  <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Company Name</label>
                  <Input value={formData.companyName} onChange={(e) => updateField('companyName', e.target.value)} placeholder="Your company" className="rounded-xl border-gray-200 font-body" required data-testid="signup-company" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Job Title</label>
                  <Input value={formData.jobTitle} onChange={(e) => updateField('jobTitle', e.target.value)} placeholder="Your role" className="rounded-xl border-gray-200 font-body" data-testid="signup-jobtitle" />
                </div>
              </>
            )}
            <div>
              <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Email</label>
              <Input type="email" value={formData.email} onChange={(e) => updateField('email', e.target.value)} placeholder="you@example.com" className="rounded-xl border-gray-200 font-body" required data-testid="signup-email" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Password</label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => updateField('password', e.target.value)} placeholder="Create a password" className="rounded-xl border-gray-200 font-body pr-10" required data-testid="signup-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#111827] font-heading block mb-1.5">Confirm Password</label>
              <Input type="password" value={formData.confirmPassword} onChange={(e) => updateField('confirmPassword', e.target.value)} placeholder="Confirm your password" className="rounded-xl border-gray-200 font-body" required data-testid="signup-confirm-password" />
            </div>

            <Button type="submit" className="w-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white rounded-full h-11 font-medium border-0 hover:shadow-lg transition-all" data-testid="signup-submit-btn">
              Create Account
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100" /></div>
            <div className="relative flex justify-center"><span className="bg-white px-4 text-xs text-gray-400 font-body">or continue with</span></div>
          </div>

          <div className="space-y-2">
            <Button variant="outline" onClick={() => toast.info('Google SSO coming soon', { description: 'Use email signup for now.' })} className="w-full rounded-full h-11 border-gray-200 font-body text-sm gap-2" data-testid="sso-google">
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </Button>
            <Button variant="outline" onClick={() => toast.info('Apple SSO coming soon', { description: 'Use email signup for now.' })} className="w-full rounded-full h-11 border-gray-200 font-body text-sm gap-2" data-testid="sso-apple">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.32 2.32-1.55 4.31-3.74 4.25z"/></svg>
              Continue with Apple
            </Button>
            <Button variant="outline" onClick={() => setWalletOpen(true)} className={`w-full rounded-full h-11 border-gray-200 font-body text-sm gap-2 ${walletConnected ? 'border-[#10B981] text-[#10B981]' : ''}`} data-testid="wallet-connect-btn">
              <Wallet className="w-4 h-4" />
              {walletConnected ? 'Wallet Connected' : 'Connect Wallet'}
              {walletConnected && <Check className="w-4 h-4" />}
            </Button>
          </div>

          <p className="text-xs text-gray-400 text-center mt-6 font-body leading-relaxed">
            By joining, you agree to our <Link to="/" className="text-[#7C3AED] hover:underline">Terms</Link>, <Link to="/" className="text-[#7C3AED] hover:underline">Privacy</Link>, and the <Link to="/principles" className="text-[#7C3AED] hover:underline">Glass Box Principles</Link>.
          </p>
        </div>

        <p className="text-center mt-6 text-sm text-gray-500 font-body">
          Already have an account? <Link to="/signin" className="text-[#7C3AED] font-medium hover:underline" data-testid="goto-signin">Sign In</Link>
        </p>
      </motion.div>

      <WalletConnectModal open={walletOpen} onOpenChange={setWalletOpen} onConnected={handleWalletConnect} />
    </div>
  );
}
